import React, { useState } from 'react';
import { Box, Typography, Button, Card, CardContent, Grid, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, IconButton, Divider, useTheme } from '@mui/material';
import { Icon } from '@iconify/react';
interface RecordUploadCardProps {
  onClose: () => void;
}
const RecordUploadCard: React.FC<RecordUploadCardProps> = ({
  onClose
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'initial' | 'preview' | 'success'>('initial');
  const [previewData, setPreviewData] = useState<any[]>([]);
  const theme = useTheme();
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };
  const handleDragLeave = () => {
    setIsDragging(false);
  };
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      if (droppedFile.name.endsWith('.xlsx')) {
        setFile(droppedFile);
        // In a real app, you would parse the Excel file here
        // For now, let's simulate a preview with mock data
        simulateFileUpload(droppedFile);
      }
    }
  };
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const selectedFile = e.target.files[0];
      if (selectedFile.name.endsWith('.xlsx')) {
        setFile(selectedFile);
        // In a real app, you would parse the Excel file here
        // For now, let's simulate a preview with mock data
        simulateFileUpload(selectedFile);
      }
    }
  };
  const simulateFileUpload = (file: File) => {
    // Simulate processing delay
    setTimeout(() => {
      // Mock preview data
      const mockData = [{
        fullName: 'John Doe',
        username: 'john.doe',
        email: 'john.doe@example.com',
        mobile: '+92 300 1234567',
        branchCode: 'LHR001',
        status: 'new'
      }, {
        fullName: 'Jane Smith',
        username: 'jane.smith',
        email: 'jane.smith@example.com',
        mobile: '+92 300 7654321',
        branchCode: 'KHI002',
        status: 'update'
      }, {
        fullName: 'Ahmed Khan',
        username: 'ahmed.khan',
        email: 'ahmed.khan@example.com',
        mobile: '+92 301 1234567',
        branchCode: 'ISB003',
        status: 'error',
        error: 'Email already exists'
      }, {
        fullName: 'Sara Ali',
        username: 'sara.ali',
        email: 'sara.ali@example.com',
        mobile: '+92 302 7654321',
        branchCode: 'LHR001',
        status: 'new'
      }, {
        fullName: 'Bilal Ahmad',
        username: 'bilal.ahmad',
        email: 'bilal.ahmad@example.com',
        mobile: '+92 303 1234567',
        branchCode: 'KHI002',
        status: 'update'
      }];
      setPreviewData(mockData);
      setUploadStatus('preview');
    }, 1000);
  };
  const handleConfirmImport = () => {
    // In a real app, this would submit the data to the backend
    setUploadStatus('success');
    // After successful import, you might want to refresh the users table
    // For now, just close the upload card after a delay
    setTimeout(() => {
      onClose();
    }, 2000);
  };
  // Count statistics
  const newCount = previewData.filter(row => row.status === 'new').length;
  const updateCount = previewData.filter(row => row.status === 'update').length;
  const errorCount = previewData.filter(row => row.status === 'error').length;
  return <Card elevation={0} sx={{
    mb: 3,
    borderRadius: 2,
    border: '1px solid #E5E7EB'
  }} data-id="element-1971" data-mp="gbn1zhdfv2i1">
      <CardContent data-id="element-1972" data-mp="lb82vxc65kxe">
        <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 2
      }} data-id="element-1973" data-mp="d8fu986cj7yw">
          <Typography variant="h6" component="h2" data-id="element-1974" data-mp="8byx5oh65j9o">
            {uploadStatus === 'initial' ? 'Upload Records' : uploadStatus === 'preview' ? 'Preview Bulk Upload' : 'Bulk Upload Successful'}
          </Typography>
          <IconButton onClick={onClose} size="small" data-id="element-1975" data-mp="3qox1s8owp0a6">
            <Icon icon="lucide:x" width={18} height={18} data-id="element-1976" data-mp="3lnzkdybq0a9q" />
          </IconButton>
        </Box>
        {uploadStatus === 'initial' && <Box sx={{
        border: `2px dashed ${isDragging ? theme.palette.primary.main : '#D1D5DB'}`,
        borderRadius: 1,
        p: 3,
        backgroundColor: isDragging ? 'rgba(14, 165, 233, 0.04)' : '#FFFFFF',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.2s ease'
      }} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop} onClick={() => document.getElementById('file-upload')?.click()} data-id="element-1977" data-mp="3gpy1bdeaoay9">
            <input id="file-upload" type="file" accept=".xlsx" onChange={handleFileChange} style={{
          display: 'none'
        }} data-id="element-1978" data-mp="32m0gdrpavypt" />
            <Icon icon="lucide:upload" width={32} height={32} color="#0EA5E9" style={{
          margin: '0 auto 16px'
        }} data-id="element-1979" data-mp="2ette6bww8xih" />
            <Typography variant="body1" sx={{
          mb: 1
        }} data-id="element-1980" data-mp="2nw2r18fmx2z1">
              Drag and drop your XLSX file here, or{' '}
              <span style={{
            color: '#0EA5E9',
            fontWeight: 500
          }} data-id="element-1981" data-mp="33zfyj8ilyx7">
                browse
              </span>
            </Typography>
            <Typography variant="body2" color="text.secondary" data-id="element-1982" data-mp="1v5k1rslc08jm">
              Only Excel (XLSX) files are supported
            </Typography>
          </Box>}
        {uploadStatus === 'preview' && <>
            <Box sx={{
          mb: 3
        }} data-id="element-1983" data-mp="247td2kofc5y2">
              <Typography variant="subtitle2" sx={{
            mb: 1
          }} data-id="element-1984" data-mp="1ghp7w8vl7y4y">
                File:{' '}
                <span style={{
              fontWeight: 400
            }} data-id="element-1985" data-mp="1q8ad4f5ro0wu">
                  {file?.name}
                </span>
              </Typography>
              <Box sx={{
            display: 'flex',
            gap: 2,
            mt: 2
          }} data-id="element-1986" data-mp="xpmz1jp6tm59">
                <Chip icon={<Icon icon="lucide:check-circle" width={16} height={16} data-id="element-1988" data-mp="34dnd1c5l0cpm" />} label={`${newCount} New`} sx={{
              backgroundColor: 'rgba(16, 185, 129, 0.1)',
              color: '#10B981',
              '.MuiChip-icon': {
                color: '#10B981'
              }
            }} data-id="element-1987" data-mp="34fghcksq2kug" />
                <Chip icon={<Icon icon="lucide:file-text" width={16} height={16} data-id="element-1990" data-mp="3s4solf4hoiu4" />} label={`${updateCount} Updates`} sx={{
              backgroundColor: 'rgba(14, 165, 233, 0.1)',
              color: '#0EA5E9',
              '.MuiChip-icon': {
                color: '#0EA5E9'
              }
            }} data-id="element-1989" data-mp="3n6her4e728bw" />
                {errorCount > 0 && <Chip icon={<Icon icon="lucide:alert-circle" width={16} height={16} data-id="element-1992" data-mp="27muvea302clq" />} label={`${errorCount} Errors`} sx={{
              backgroundColor: 'rgba(239, 68, 68, 0.1)',
              color: '#EF4444',
              '.MuiChip-icon': {
                color: '#EF4444'
              }
            }} data-id="element-1991" data-mp="27p7it2rj1f66" />}
              </Box>
            </Box>
            <TableContainer component={Paper} variant="outlined" sx={{
          border: '1px solid #E5E7EB',
          mb: 3
        }} data-id="element-1993" data-mp="3i27397lih4k9">
              <Table size="small" data-id="element-1994" data-mp="38b46ghzm7bqs">
                <TableHead data-id="element-1995" data-mp="33em39mjxnlgk">
                  <TableRow data-id="element-1996" data-mp="33dazvnuegfpz">
                    <TableCell data-id="element-1997" data-mp="33bzvblt4r3xt">Status</TableCell>
                    <TableCell data-id="element-1998" data-mp="4odh64ml8xmr">Full Name</TableCell>
                    <TableCell data-id="element-1999" data-mp="4px062zmpms8">Username</TableCell>
                    <TableCell data-id="element-2000" data-mp="3w1qbe0ebofwk">Email Address</TableCell>
                    <TableCell data-id="element-2001" data-mp="3w0yi4w876u9g">Mobile</TableCell>
                    <TableCell data-id="element-2002" data-mp="79l7ve1kyz0v">Branch Code</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody data-id="element-2003" data-mp="c8egot5xa1up">
                  {previewData.map((row, index) => <TableRow key={index} sx={{
                backgroundColor: row.status === 'error' ? 'rgba(239, 68, 68, 0.05)' : 'inherit'
              }} data-id="element-2004" data-mp="h5o9misisvna" data-mpi={index}>
                      <TableCell data-id="element-2005" data-mp="v4o0lx3i9mmm">
                        {row.status === 'new' && <Chip label="New" size="small" sx={{
                    backgroundColor: 'rgba(16, 185, 129, 0.1)',
                    color: '#10B981',
                    height: 24
                  }} data-id="element-2006" data-mp="zxpimdz0e2af" />}
                        {row.status === 'update' && <Chip label="Update" size="small" sx={{
                    backgroundColor: 'rgba(14, 165, 233, 0.1)',
                    color: '#0EA5E9',
                    height: 24
                  }} data-id="element-2007" data-mp="h2r9jbgqzlmv" />}
                        {row.status === 'error' && <Chip label="Error" size="small" sx={{
                    backgroundColor: 'rgba(239, 68, 68, 0.1)',
                    color: '#EF4444',
                    height: 24
                  }} data-id="element-2008" data-mp="zrxkgcbav8y4" />}
                      </TableCell>
                      <TableCell data-id="element-2009" data-mp="3bkw3me1d5hwr">{row.fullName}</TableCell>
                      <TableCell data-id="element-2010" data-mp="340bs0pf28jip">{row.username}</TableCell>
                      <TableCell data-id="element-2011" data-mp="2z93hfq1zus3y">{row.email}</TableCell>
                      <TableCell data-id="element-2012" data-mp="2z7ip8735jl99">{row.mobile}</TableCell>
                      <TableCell data-id="element-2013" data-mp="2u8xn224cari6">{row.branchCode}</TableCell>
                    </TableRow>)}
                </TableBody>
              </Table>
            </TableContainer>
            {errorCount > 0 && <Box sx={{
          mb: 3,
          p: 2,
          backgroundColor: 'rgba(239, 68, 68, 0.05)',
          borderRadius: 1
        }} data-id="element-2014" data-mp="3mx39queyeqkn">
                <Typography variant="body2" color="error" sx={{
            display: 'flex',
            alignItems: 'center'
          }} data-id="element-2015" data-mp="3cydrh0j5b43v">
                  <Icon icon="lucide:alert-circle" width={16} height={16} style={{
              marginRight: 8
            }} data-id="element-2016" data-mp="1wm8c32rnt4k6" />
                  Please fix the errors before uploading
                </Typography>
              </Box>}
          </>}
        {uploadStatus === 'success' && <Box sx={{
        textAlign: 'center',
        py: 3
      }} data-id="element-2017" data-mp="jmdkaino7f26">
            <Icon icon="lucide:check-circle" width={48} height={48} color="#10B981" style={{
          margin: '0 auto 16px'
        }} data-id="element-2018" data-mp="4wz9r78cyrl7" />
            <Typography variant="h6" data-id="element-2019" data-mp="3s1f57moemssy">Bulk Upload Successful</Typography>
            <Typography variant="body2" color="text.secondary" data-id="element-2020" data-mp="xgrykbu2wml">
              {newCount} new records and {updateCount} updates have been
              processed
            </Typography>
          </Box>}
        <Divider sx={{
        my: 2
      }} data-id="element-2021" data-mp="3n3dp9am9z9lp" />
        <Box sx={{
        display: 'flex',
        justifyContent: 'flex-end',
        gap: 2
      }} data-id="element-2022" data-mp="33j50ldtsez8l">
          <Button variant="outlined" onClick={onClose} data-id="element-2023" data-mp="2zj4i9i3ki3w9">
            Cancel
          </Button>
          {uploadStatus === 'preview' && <Button variant="contained" onClick={handleConfirmImport} disabled={errorCount > 0} data-id="element-2024" data-mp="38h6qahlpxwt1">
              Confirm Upload
            </Button>}
        </Box>
      </CardContent>
    </Card>;
};
export default RecordUploadCard;